import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useSearchParams } from 'react-router-dom'

import { useErrorHandler } from '../../hooks/useErrorHandler'
import { usePSNavigate } from '../../hooks/usePSNavigate'
import { Contract } from '../../models/Contract'
import { RecordFactory } from '../../models/RecordFactory'
import { useHistoryTrackerContext } from '../../providers/HistoryTrackerProvider'
import { okResult } from '../../types/utils'
import { getEditionID } from '../../utils/users'
import { PSApplication } from '../app/PSApplication'
import { ContractsPage } from '../items/ContractsPage'
import { WorkflowSelectListSkeleton } from '../common/skeletons/WorkflowSelectListSkeleton'

export function SetupDefaultContractsPage() {
    const me = PSApplication.me()
    const { logAndDisplayError } = useErrorHandler()
    const [searchParams] = useSearchParams()
    const navigate = usePSNavigate()
    const editionID = getEditionID(me)

    const { findLastHistoryEntry } = useHistoryTrackerContext()
    const lastHistoryEntry = findLastHistoryEntry({ excluding: /^\/settings\/.*\/.+/ })

    const queryClient = useQueryClient()
    const { data, isLoading } = useQuery({
        queryKey: ['defaults', 'contracts', editionID],
        queryFn: async () => {
            const recordIDs = searchParams.get('recordIDs')?.split(',') ?? []
            const editionID = getEditionID(me)

            const [edition, installedContracts] = await Promise.all([
                RecordFactory.getInstance().loadEdition(editionID),
                RecordFactory.getInstance().loadContracts(),
            ])

            const allContracts = edition?.defaultContracts ?? []

            const installedContractsFromSameEdition = installedContracts.filter(
                (c) => c.edition === editionID
            )

            const contracts = allContracts
                .filter((c) => (recordIDs.length > 0 ? recordIDs.includes(c.id ?? '') : true))
                .filter(
                    (c) =>
                        installedContractsFromSameEdition.find(
                            (saved) => saved.editionContract === c.id
                        ) === undefined
                )

            const contractsToSelect = contracts.map((c) => ({
                contract: c,
                selected: false,
            }))

            return { contracts: contractsToSelect, edition }
        },
    })

    const onNextHandler = async (selectedContracts: Contract[]) => {
        const clonedContracts = selectedContracts.map((c) => {
            const clone = c.clone()
            clone.editionContract = c.id
            clone.edition = editionID
            clone.id = undefined
            return clone
        })

        const result = await RecordFactory.getInstance().sendOnboardingContracts(
            clonedContracts,
            editionID,
            false
        )

        if (!result.ok) {
            logAndDisplayError(result.error)
            return result
        }

        queryClient.invalidateQueries({
            queryKey: ['defaults'],
        })

        // contracts are the last step in default setup, so navigate to the initial step
        const initialStep = searchParams.get('initialStep') ?? 'contracts'
        navigate(`/settings/${initialStep}`)
        return okResult(null)
    }

    if (isLoading || !data) {
        return (
            <WorkflowSelectListSkeleton
                title=" "
                subtitle="Attach forms to your offerings to automatically send when clients book, when
                    applicable."
                onClose={() => {}}
            />
        )
    }

    return (
        <ContractsPage
            contracts={data.contracts}
            onSave={onNextHandler}
            onBack={{
                title: 'Contracts',
                onClick: () => navigate('/settings/contracts'),
            }}
            onClose={() => {
                if (lastHistoryEntry) {
                    lastHistoryEntry.goBackTo()
                }
                else {
                    navigate('/')
                }
            }}
        />
    )
}
